import { TimelineAvatar } from '../base/interfaces/timeline.interface';

/**
 * Palette d'avatars par défaut, partagée entre la facture, la supervision résolue et
 * toute vue qui affiche un pastille utilisateur. Un module ayant besoin d'une palette
 * différente (ex. supervision arborescente, teintes plus sombres) la passe en argument.
 */
export const DEFAULT_AVATAR_PALETTE = ['#1e88e5', '#8e24aa', '#00897b', '#f4511e', '#3949ab', '#00acc1', '#7cb342'];

/** Utilisateur minimal accepté par les helpers d'avatar (structurellement compatible partout). */
export interface AvatarUser {
  _id?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
}

/** Couleur d'avatar déterministe dérivée d'un identifiant (hash * 31 modulo taille de palette). */
export function getAvatarColor(id: string | null | undefined, palette: string[] = DEFAULT_AVATAR_PALETTE): string {
  let hash = 0;
  for (const char of id ?? '') {
    hash = (hash * 31 + char.charCodeAt(0)) % palette.length;
  }
  return palette[hash];
}

/** Initiales majuscules à partir du prénom + nom (première lettre de chacun). */
export function getInitials(firstName?: string, lastName?: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

/** Nom affichable : `fullName` si présent, sinon recomposé depuis prénom + nom. */
export function getUserName(user: AvatarUser): string {
  return user.fullName ?? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
}

/**
 * Vue avatar précalculée (nom + initiales + couleur). null si l'utilisateur est absent —
 * pratique pour un binding direct (`[currentOwner]="toAvatar(entity.owner)"`).
 */
export function toAvatar(user?: AvatarUser | null, palette: string[] = DEFAULT_AVATAR_PALETTE): TimelineAvatar | null {
  if (!user) return null;
  return {
    name: getUserName(user),
    initials: getInitials(user.firstName, user.lastName),
    color: getAvatarColor(user._id, palette),
  };
}
